import ENDPOINTS from 'api/endpoints';
import { doDelete, doGet, doPatch, doPost } from 'api/request';
import { LOGGER_TYPE } from 'api/types';
import { generateNextPath } from 'lib/routeHelper';
import type {
  ConversationScopeKey,
  ConversationsResponse,
  CreateViewRequest,
  CustomMetricValues,
  LiveConversation,
  LiveConversationsResponse,
  PatchUserSettingsRequest,
  UpdateViewRequest,
  UserSettingsResponse,
  ViewResponse,
} from './types';

export const PAGE_SIZE = 20;

const PREFIX = 'nebula' as const;

// -- Conversations ----------------------------------------------------------

export const getConversationsV2 = (
  accountId: string,
  projectId: string,
  query: string,
  cursor?: string,
  limit: number = PAGE_SIZE,
) => {
  return doGet<ConversationsResponse>(
    generateNextPath(ENDPOINTS.CONVERSATIONS, { accountId, projectId }),
    LOGGER_TYPE.CONVERSATIONS,
    {
      params: { limit, ...(cursor && { cursor }) },
      query,
      prefix: PREFIX,
      version: 'v2',
    },
  );
};

// -- Metrics ----------------------------------------------------------------

export const getMetrics = (accountId: string, projectId: string) => {
  return doGet<CustomMetricValues[]>(
    generateNextPath(ENDPOINTS.CUSTOM_METRICS, { accountId, projectId }),
    LOGGER_TYPE.CONVERSATIONS,
    { prefix: PREFIX },
  );
};

// -- Views ------------------------------------------------------------------

export const getViews = (
  accountId: string,
  projectId: string,
  scope: ConversationScopeKey,
) => {
  return doGet<{ views: ViewResponse[] }>(
    generateNextPath(ENDPOINTS.CONVERSATIONS_REVIEW_SCOPED_VIEWS, {
      accountId,
      projectId,
      scope,
    }),
    LOGGER_TYPE.JUPITER,
    { prefix: PREFIX },
  );
};

export const createView = (
  accountId: string,
  projectId: string,
  scope: ConversationScopeKey,
  body: CreateViewRequest,
) => {
  return doPost<ViewResponse>(
    generateNextPath(ENDPOINTS.CONVERSATIONS_REVIEW_SCOPED_VIEWS, {
      accountId,
      projectId,
      scope,
    }),
    LOGGER_TYPE.JUPITER,
    body,
    { prefix: PREFIX },
  );
};

export const updateView = (
  accountId: string,
  projectId: string,
  scope: ConversationScopeKey,
  viewId: string,
  body: UpdateViewRequest,
) => {
  return doPatch<ViewResponse>(
    generateNextPath(ENDPOINTS.CONVERSATIONS_REVIEW_SCOPED_VIEW, {
      accountId,
      projectId,
      scope,
      viewId,
    }),
    LOGGER_TYPE.JUPITER,
    body,
    { prefix: PREFIX },
  );
};

export const deleteView = (
  accountId: string,
  projectId: string,
  scope: ConversationScopeKey,
  viewId: string,
) => {
  return doDelete<void>(
    generateNextPath(ENDPOINTS.CONVERSATIONS_REVIEW_SCOPED_VIEW, {
      accountId,
      projectId,
      scope,
      viewId,
    }),
    LOGGER_TYPE.JUPITER,
    {},
    { prefix: PREFIX },
  );
};

// -- User Settings ----------------------------------------------------------

export const getUserSettings = (
  accountId: string,
  projectId: string,
  scope: ConversationScopeKey,
) => {
  return doGet<UserSettingsResponse>(
    generateNextPath(ENDPOINTS.CONVERSATIONS_REVIEW_SCOPED_SETTINGS, {
      accountId,
      projectId,
      scope,
    }),
    LOGGER_TYPE.JUPITER,
    { prefix: PREFIX },
  );
};

export const patchUserSettings = (
  accountId: string,
  projectId: string,
  scope: ConversationScopeKey,
  body: PatchUserSettingsRequest,
) => {
  return doPatch<UserSettingsResponse>(
    generateNextPath(ENDPOINTS.CONVERSATIONS_REVIEW_SCOPED_SETTINGS, {
      accountId,
      projectId,
      scope,
    }),
    LOGGER_TYPE.JUPITER,
    body,
    { prefix: PREFIX },
  );
};

// -- Live Conversations -----------------------------------------------------

export const getLiveConversations = (
  accountId: string,
  projectId: string,
  channel: string,
  cursor?: string,
  limit: number = PAGE_SIZE,
  includeTotalCount: boolean = false,
) => {
  return doGet<LiveConversationsResponse>(
    generateNextPath(ENDPOINTS.LIVE_CONVERSATIONS, { accountId, projectId }),
    LOGGER_TYPE.CONVERSATIONS,
    {
      params: {
        channel,
        limit,
        include_total_count: includeTotalCount,
        ...(cursor && { cursor }),
      },
      prefix: PREFIX,
      version: 'v2',
    },
  );
};

export const getLiveConversation = (
  accountId: string,
  projectId: string,
  conversationId: string,
) => {
  return doGet<LiveConversation>(
    generateNextPath(ENDPOINTS.LIVE_CONVERSATION, {
      accountId,
      projectId,
      conversationId,
    }),
    LOGGER_TYPE.CONVERSATIONS,
    {
      prefix: PREFIX,
      version: 'v2',
    },
  );
};

// -- Export -----------------------------------------------------------------

export const getConversationsExport = ({
  accountId,
  projectId,
  query,
  includeAnnotations,
  selectedColumns,
}: {
  accountId: string;
  projectId: string;
  query: string;
  includeAnnotations: boolean;
  selectedColumns?: string[];
}) => {
  const params: Record<string, any> = {
    include_annotations: includeAnnotations,
  };

  if (selectedColumns?.length) {
    params['columns'] = selectedColumns;
  }

  return doGet<{ data: Blob; headers: Headers; statusCode: number }>(
    `accounts/${accountId}/projects/${projectId}/conversations/export`,
    LOGGER_TYPE.CONVERSATIONS,
    {
      query,
      params,
      downloadingFile: true,
      prefix: PREFIX,
      returnStatusCode: true,
    },
  );
};
